import React, { useId } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Flex, Heading, Label, Switch, Text, TextLink } from '@indeed/ifl-components';
import { FileDoc, NewWindow } from '@indeed/ifl-icons';
import GettextFactory from '../../../util/GettextFactory';
import { useEventLogger } from '../../../lib/eventLogger/EventLoggerProvider';

export interface ApplicationQuestionsSectionProps {
    /** Whether the SQ (screening question) prefill toggle is on. Default ON. */
    sqOptedIn: boolean;
    /** Updates the local SQ toggle state. Persisted by ReviewPage on Update. */
    onSqOptInChange: (checked: boolean) => void;
    /** Whether the DQ (voluntary self-identification) prefill toggle is on. Default OFF. */
    dqOptedIn: boolean;
    /** Updates the local DQ toggle state. Persisted by ReviewPage on Update. */
    onDqOptInChange: (checked: boolean) => void;
}

/**
 * The DQ (voluntary self-identification) autofill toggle is intentionally hidden
 * for this phase pending the final product requirement (IAFE-2517). All DQ code
 * and wiring is kept intact (props here, state/persistence in ReviewPage) so the
 * toggle can be re-enabled by flipping this flag to `true` once the PRD lands.
 */
const SHOW_DQ_TOGGLE = false;

/**
 * Application questions section for the progressive-automation Review variant
 * (rnhp_auto_apply_banner_tst === 2). Renders two opt-in toggles whose changes
 * update local state only; ReviewPage persists them together on Update:
 * - SQ: "Autofill application questions" (default ON)
 * - DQ: "Autofill voluntary self-identification questions" (default OFF) — hidden
 *   this phase pending the final PRD (IAFE-2517); see SHOW_DQ_TOGGLE above.
 *
 * Figma: https://www.figma.com/design/YqvUoqcp7hUVEjilAGM0ls/Auto-Apply-2026?node-id=19431-64136
 * JIRA: IAFE-2080, IAFE-2517
 */
export const ApplicationQuestionsSection: React.FC<ApplicationQuestionsSectionProps> = ({
    sqOptedIn,
    onSqOptInChange,
    dqOptedIn,
    onDqOptInChange,
}) => {
    const gt = GettextFactory();
    const location = useLocation();
    const eventLogger = useEventLogger();
    const sqSwitchId = useId();
    const dqSwitchId = useId();

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Flex sx={{ gap: 2, alignItems: 'center' }}>
                <Box sx={{ flexShrink: 0 }}>
                    <FileDoc size="md" aria-hidden />
                </Box>
                <Heading level={3} as="h2" sx={{ marginBlockEnd: 0 }}>
                    {gt.gettext('Application questions')}
                </Heading>
            </Flex>
            <Box
                sx={{
                    border: '1px solid',
                    borderColor: 'border.weak',
                    borderRadius: 'lg',
                    padding: 4,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 4,
                }}
            >
                {/* SQ (screening question) prefill toggle - default ON */}
                <Flex sx={{ gap: 2, alignItems: 'flex-start', justifyContent: 'space-between' }}>
                    <Label htmlFor={sqSwitchId} sx={{ flex: 1, fontWeight: '700' }}>
                        {gt.gettext('Autofill application questions')}
                    </Label>
                    <Switch
                        as="span"
                        id={sqSwitchId}
                        checked={sqOptedIn}
                        sx={{ padding: 0 }}
                        onChange={(e) => onSqOptInChange(e.target.checked)}
                    />
                </Flex>

                {/* DQ (voluntary self-identification) prefill toggle - default OFF.
                    Hidden this phase pending the final PRD (IAFE-2517); kept intact
                    behind SHOW_DQ_TOGGLE so it can be re-enabled later. */}
                {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
                {SHOW_DQ_TOGGLE && (
                    <Flex
                        sx={{ gap: 2, alignItems: 'flex-start', justifyContent: 'space-between' }}
                    >
                        <Label htmlFor={dqSwitchId} sx={{ flex: 1, fontWeight: '700' }}>
                            {gt.gettext('Autofill voluntary self-identification questions')}
                        </Label>
                        <Switch
                            as="span"
                            id={dqSwitchId}
                            checked={dqOptedIn}
                            sx={{ padding: 0 }}
                            onChange={(e) => onDqOptInChange(e.target.checked)}
                        />
                    </Flex>
                )}

                {/* Helper text + review link */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Text level={2} variant="secondary">
                        {gt.gettext(
                            'AI will autofill your applications using your past and future answers to employer questions. AI can make mistakes, so review before you continue.',
                        )}
                    </Text>
                    <Box>
                        <TextLink
                            href="/auto-apply-setup/past-answers?from=auto-apply-setup"
                            target="_blank"
                            rel="noopener"
                            iconAfter={<NewWindow aria-hidden />}
                            aria-label={gt.gettext(
                                'Review your past answers (opens in a new window)',
                            )}
                            sx={{ fontSize: 2 }}
                            onClick={() => {
                                eventLogger.logAutoApplySetupButtonClickEvent?.({
                                    currentPage: location.pathname,
                                    button: 'view-past-answers',
                                });
                            }}
                        >
                            {gt.gettext('Review your past answers')}
                        </TextLink>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};
